import React, { useState, useEffect } from 'react'
import { 
  Zap, 
  RefreshCw, 
  Search,
  Play,
  BookOpen,
  Terminal,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react'
import { apiFetch } from '../config'

const Skills = () => {
  const [skills, setSkills] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [selectedSkill, setSelectedSkill] = useState(null)
  const [launching, setLaunching] = useState(false)
  const [launchStatus, setLaunchStatus] = useState(null)

  useEffect(() => {
    fetchSkills()
  }, [])

  const fetchSkills = async () => {
    try {
      setLoading(true)
      const response = await apiFetch("/skills")
      const data = await response.json()
      setSkills(data.skills || [])
      setCategories(data.categories || [])
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch skills:', error)
      setLoading(false)
    }
  }

  const launchSkill = async (skillId) => {
    try {
      setLaunching(true)
      setLaunchStatus(null)
      
      const response = await apiFetch(`/skills/${skillId}/launch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      
      const data = await response.json()
      
      if (response.ok) {
        setLaunchStatus({ type: 'success', message: data.message })
        fetchSkills() // Refresh to get updated launch count
      } else {
        setLaunchStatus({ type: 'error', message: data.error || 'Failed to launch' })
      }
      
      setTimeout(() => setLaunchStatus(null), 3000)
      setLaunching(false)
    } catch (error) {
      console.error('Failed to launch skill:', error)
      setLaunchStatus({ type: 'error', message: error.message })
      setLaunching(false)
    }
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'Core Business': '💼',
      'SISTL Operations': '🚛',
      'Trading': '📈',
      'Marketing': '📢',
      'Management': '📊',
      'Security': '🔒',
      'Voice': '🎤',
      'Development': '💻',
      'AI': '🧠'
    }
    return icons[category] || '⚡'
  }

  const getCategoryColor = (category) => {
    const colors = {
      'Core Business': 'bg-blue-500',
      'SISTL Operations': 'bg-green-500',
      'Trading': 'bg-yellow-500',
      'Marketing': 'bg-purple-500',
      'Management': 'bg-indigo-500',
      'Security': 'bg-red-500',
      'Voice': 'bg-pink-500',
      'Development': 'bg-cyan-500',
      'AI': 'bg-violet-500'
    }
    return colors[category] || 'bg-slate-500'
  }

  const filteredSkills = skills.filter(skill => {
    const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory
    const matchesSearch = !searchQuery || 
      skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      skill.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">SISTL Skills</h1>
            <p className="text-slate-400 mt-1">{skills.length} skills ready to launch</p>
          </div>
          
          <button 
            onClick={fetchSkills}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
              selectedCategory === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            All ({skills.length})
          </button>
          
          {categories.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors flex items-center gap-2 ${
                selectedCategory === cat.category
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <span>{getCategoryIcon(cat.category)}</span>
              {cat.category} ({cat.count})
            </button>
          ))}
        </div>
      </div>

      {/* Launch Status Toast */}
      {launchStatus && (
        <div className={`mx-6 mt-4 p-4 rounded-lg flex items-center gap-3 ${
          launchStatus.type === 'success' ? 'bg-green-900/50 border border-green-700' : 'bg-red-900/50 border border-red-700'
        }`}>
          {launchStatus.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-400" />
          )}
          <span className={launchStatus.type === 'success' ? 'text-green-200' : 'text-red-200'}>
            {launchStatus.message}
          </span>
        </div>
      )}

      {/* Skills Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredSkills.map((skill) => (
            <div 
              key={skill.id}
              className="bg-slate-800 border border-slate-700 rounded-lg p-5 hover:border-indigo-500 transition-all cursor-pointer group"
              onClick={() => setSelectedSkill(skill)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${getCategoryColor(skill.category)}`}>
                    {getCategoryIcon(skill.category)}
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-indigo-400 transition-colors">{skill.name}</h3>
                    <span className="text-xs text-slate-400">{skill.category}</span>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-xs text-slate-500">Launched</p>
                  <p className="text-lg font-bold text-indigo-400">{skill.launch_count || 0}</p>
                </div>
              </div>
              
              <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                {skill.description || 'No description available'}
              </p>
              
              <div className="flex items-center justify-between">
                <code className="text-xs bg-slate-900 px-2 py-1 rounded text-slate-500 truncate flex-1 mr-3">
                  {skill.command}
                </code>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    launchSkill(skill.id)
                  }}
                  disabled={launching}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 rounded text-white text-sm transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Launch
                </button>
              </div>            </div>
          ))}
        </div>

        {filteredSkills.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No skills found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Skill Detail Modal */}
      {selectedSkill && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${getCategoryColor(selectedSkill.category)}`}>
                  {getCategoryIcon(selectedSkill.category)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedSkill.name}</h2>
                  <span className="text-sm text-slate-400">{selectedSkill.category}</span>
                </div>
              </div>
              
              <button 
                onClick={() => setSelectedSkill(null)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Description</h3>
                <p className="text-slate-400">{selectedSkill.description || 'No description available'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Launch Count</p>
                  <p className="text-2xl font-bold text-indigo-400">{selectedSkill.launch_count || 0}</p>
                </div>
                
                <div className="bg-slate-700/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Last Launched</p>
                  <p className="text-white">
                    {selectedSkill.last_launched 
                      ? new Date(selectedSkill.last_launched).toLocaleString() 
                      : 'Never'}
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Command</h3>
                <div className="bg-slate-900 p-4 rounded-lg flex items-center gap-3">
                  <Terminal className="w-5 h-5 text-slate-500" />
                  <code className="text-green-400 font-mono">{selectedSkill.command}</code>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Location</h3>
                <p className="text-slate-400 text-sm font-mono">{selectedSkill.location}</p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => launchSkill(selectedSkill.id)}
                  disabled={launching}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-white font-medium transition-colors"
                >
                  <Play className="w-5 h-5" />
                  {launching ? 'Launching...' : 'Launch Skill'}
                </button>
                
                {selectedSkill.documentation && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      alert(`Documentation: ${selectedSkill.documentation}`)
                    }}
                    className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                  >
                    <BookOpen className="w-5 h-5" />
                    Docs
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Skills
